
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Bell, Info, AlertTriangle, CheckCircle2, Zap, Clock, Volume2, VolumeX, LineChart } from 'lucide-react';
import { cn } from '../lib/utils';
import { formatAlertTime } from '../lib/alertTime';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'alert' | 'success' | 'ai';
  timestamp: string;
  // ADV-MKT-ALERT-TIMESTAMP-AUDIO-SMS-001 — epoch ms of the alert; when present the panel
  // shows an exact ET clock time + a live-updating relative age instead of a static label.
  createdAtMs?: number;
  // ADV-ALERTS-SYMBOL-CLICK-CHART-001 — when present, the alert title renders as a
  // clickable ticker that opens the symbol's chart/detail view (navigation only).
  symbol?: string;
  read: boolean;
}

interface NotificationCenterProps {
  isOpen: boolean;
  onClose: () => void;
  notifications: Notification[];
  onMarkAsRead: (id: string) => void;
  onClearAll: () => void;
  soundEnabled?: boolean;
  onToggleSound?: () => void;
  // ADV-ALERTS-SYMBOL-CLICK-CHART-001 — open the chart/detail view for an alert's symbol.
  onSymbolClick?: (symbol: string) => void;
}

export function NotificationCenter({ isOpen, onClose, notifications, onMarkAsRead, onClearAll, soundEnabled, onToggleSound, onSymbolClick }: NotificationCenterProps) {
  // ADV-MKT-ALERT-TIMESTAMP-AUDIO-SMS-001 — 1s tick so relative ages update live while open.
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  useEffect(() => {
    if (!isOpen) return;
    setNowMs(Date.now());
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [isOpen]);
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]"
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed top-0 right-0 h-full w-full max-w-sm bg-[var(--card-bg)] shadow-2xl z-[70] border-l border-[var(--card-border)] flex flex-col"
          >
            <div className="p-6 border-b border-[var(--card-border)] flex items-center justify-between bg-[var(--card-header-bg)]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
                  <Bell size={20} className="text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-lg font-black text-[var(--app-text)] uppercase italic tracking-tight">Intelligence Center</h2>
                  <p className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Real-time alerts & AI insights</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {/* ADV-MKT-ALERT-TIMESTAMP-AUDIO-SMS-001 — user-controlled BUY-alert sound toggle. */}
                {onToggleSound && (
                  <button
                    onClick={onToggleSound}
                    data-testid="notif-sound-toggle"
                    data-sound-enabled={soundEnabled ? 'true' : 'false'}
                    title={soundEnabled ? 'BUY CANDIDATE sound alerts: ON (click to mute)' : 'BUY CANDIDATE sound alerts: OFF (click to enable)'}
                    aria-label={soundEnabled ? 'Mute BUY candidate sound alerts' : 'Enable BUY candidate sound alerts'}
                    className={cn('p-2 rounded-full transition-colors', soundEnabled ? 'text-emerald-600 hover:bg-emerald-500/10' : 'text-[var(--muted-text)] hover:bg-[var(--input-bg)]')}
                  >
                    {soundEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
                  </button>
                )}
                <button onClick={onClose} className="p-2 hover:bg-[var(--input-bg)] rounded-full transition-colors">
                  <X size={20} className="text-[var(--muted-text)]" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-hide">
              {notifications.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center p-8 space-y-4 opacity-40">
                  <Bell size={48} className="text-[var(--muted-text)]" />
                  <p className="text-xs font-black uppercase italic tracking-widest">No active notifications</p>
                </div>
              ) : (
                notifications.map((notification) => (
                  <motion.div
                    key={notification.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={cn(
                      "p-4 rounded-2xl border transition-all relative group",
                      notification.read ? "bg-[var(--card-bg)] border-[var(--card-border)]" : "bg-emerald-500/5 border-emerald-500/20 shadow-sm shadow-emerald-500/5"
                    )}
                    onClick={() => onMarkAsRead(notification.id)}
                  >
                    {!notification.read && (
                      <div className="absolute top-4 right-4 w-2 h-2 bg-emerald-500 rounded-full" />
                    )}
                    <div className="flex gap-3">
                      <div className={cn(
                        "w-8 h-8 rounded-lg flex items-center justify-center shrink-0",
                        notification.type === 'info' && "bg-blue-500/10 text-blue-600",
                        notification.type === 'alert' && "bg-rose-500/10 text-rose-600",
                        notification.type === 'success' && "bg-emerald-500/10 text-emerald-600",
                        notification.type === 'ai' && "bg-amber-500/10 text-amber-600"
                      )}>
                        {notification.type === 'info' && <Info size={16} />}
                        {notification.type === 'alert' && <AlertTriangle size={16} />}
                        {notification.type === 'success' && <CheckCircle2 size={16} />}
                        {notification.type === 'ai' && <Zap size={16} />}
                      </div>
                      <div className="space-y-1">
                        {/* ADV-ALERTS-SYMBOL-CLICK-CHART-001 — clickable ticker/title opens the
                            symbol's chart/detail view (same route as the Markets board). Falls
                            back to a plain heading when the alert carries no symbol. stopPropagation
                            keeps the card's mark-as-read from firing; navigation only, never a trade. */}
                        {notification.symbol && onSymbolClick ? (
                          <button
                            type="button"
                            data-testid={`notif-symbol-open-${notification.symbol}`}
                            data-symbol={notification.symbol}
                            onClick={(e) => { e.stopPropagation(); onSymbolClick(notification.symbol as string); }}
                            title={`Open ${notification.symbol} chart`}
                            aria-label={`Open ${notification.symbol} chart`}
                            className="group/sym inline-flex items-center gap-1.5 text-left text-xs font-black text-[var(--app-text)] uppercase italic tracking-tight hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors cursor-pointer"
                          >
                            <span>{notification.title}</span>
                            <LineChart size={12} className="opacity-50 group-hover/sym:opacity-100 transition-opacity shrink-0" />
                          </button>
                        ) : (
                          <h4 className="text-xs font-black text-[var(--app-text)] uppercase italic tracking-tight">{notification.title}</h4>
                        )}
                        <p className="text-[11px] text-[var(--muted-text)] leading-relaxed font-medium">{notification.message}</p>
                        <div data-testid="notif-timestamp" className="flex items-center gap-1.5 text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest pt-1">
                          <Clock size={10} />
                          {typeof notification.createdAtMs === 'number'
                            ? formatAlertTime(notification.createdAtMs, nowMs)
                            : notification.timestamp}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            {notifications.length > 0 && (
              <div className="p-4 border-t border-[var(--card-border)] bg-[var(--card-header-bg)]">
                <button 
                  onClick={onClearAll}
                  className="w-full py-3 bg-[var(--input-bg)] hover:bg-[var(--card-border)] text-[var(--app-text)] rounded-xl text-[10px] font-black uppercase italic transition-all border border-[var(--input-border)]"
                >
                  Clear All Notifications
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
